import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('era_auth_token');
    if (token) {
      verifySession(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifySession = async (token) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.authenticated) {
        setUser({
          username: response.data.username,
          email: response.data.email,
          role: response.data.role,
          token
        });
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password
      });
      
      const { token, username: user, email, role } = response.data;
      localStorage.setItem('era_auth_token', token);
      setUser({ username: user, email, role, token });
      return { success: true };
      
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, fullName, gdprConsent, country = null, hp_confirm_token = '') => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email,
        password,
        full_name: fullName,
        gdpr_consent: gdprConsent,
        country: country,
        hp_confirm_token: hp_confirm_token
      });

      const { token, username, verification_email_sent, email: userEmail } = response.data;
      
      // Safety net: if token is empty (honeypot fired for real user), show error
      if (!token) {
        return { success: false, error: 'Registration could not be completed. Please contact support.' };
      }
      
      localStorage.setItem('era_auth_token', token);
      setUser({ username, email, role: 'viewer', token });
      return { success: true, verification_email_sent, email: userEmail };

    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google`, {
        id_token: idToken
      });
      const { token, username, email, role } = response.data;
      localStorage.setItem('era_auth_token', token);
      setUser({ username, email, role, token });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Google sign-in failed'
      };
    }
  };

  const loginWithApple = async (idToken, fullName) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/apple`, {
        id_token: idToken,
        full_name: fullName || undefined
      });
      const { token, username, email, role } = response.data;
      localStorage.setItem('era_auth_token', token);
      setUser({ username, email, role, token });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Apple sign-in failed'
      };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('era_auth_token');
    setUser(null);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('era_auth_token');
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.warn('Logout endpoint failed:', e);
      // Logout locally even if server call fails
    }
    handleLogout();
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, loginWithGoogle, loginWithApple, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
